import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useState } from 'react'

import { getGetEmployersRenewalsRenewalConfigsIdQueryKey as getRenewalConfigQueryKey, usePostQuotingProspectsProspectIdQuotesIdGenerate as useGenerateQuote } from 'utils/web_api'

import { useEmployerRenewalConfig } from '../providers/EmployerRenewalConfigProvider'

type GenerateError = 'quote_not_found' | 'generate_error'

export default function useGenerateRenewalQuotes () {
  const { employerRenewalConfig, isUsingFlatAllowances } = useEmployerRenewalConfig()
  const queryClient = useQueryClient()

  const [error, setError] = useState<GenerateError>()

  const generateCurrentYearQuoteMutation = useGenerateQuote()
  const generateRenewalYearQuoteMutation = useGenerateQuote()

  const generateRenewalQuotes = useCallback(async () => {
    const currentYearQuote = employerRenewalConfig?.current_year_quote
    const renewalYearQuote = employerRenewalConfig?.renewal_year_quote

    if (!currentYearQuote || !renewalYearQuote) {
      setError('quote_not_found')
      return
    }

    try {
      const promises = [
        generateCurrentYearQuoteMutation.mutateAsync({
          prospectId: employerRenewalConfig.prospect_id,
          id: currentYearQuote.id,
          data: {
            coverage_percentiles: isUsingFlatAllowances ? undefined : currentYearQuote.coverage_percentiles,
            metal_tiers: isUsingFlatAllowances ? undefined : currentYearQuote.metal_tiers,
            coverage_start_date: currentYearQuote.coverage_start_date ?? '',
            year: currentYearQuote.year
          }
        }),
        generateRenewalYearQuoteMutation.mutateAsync({
          prospectId: employerRenewalConfig.prospect_id,
          id: renewalYearQuote.id,
          data: {
            coverage_percentiles: isUsingFlatAllowances ? undefined : renewalYearQuote.coverage_percentiles,
            metal_tiers: isUsingFlatAllowances ? undefined : renewalYearQuote.metal_tiers,
            coverage_start_date: renewalYearQuote.coverage_start_date ?? '',
            year: renewalYearQuote.year
          }
        })
      ]

      const results = await Promise.all(promises)

      await queryClient.invalidateQueries({
        queryKey: getRenewalConfigQueryKey(employerRenewalConfig.id)
      })

      return results
    } catch (error) {
      console.error(error)
      setError('generate_error')
    }
  }, [generateCurrentYearQuoteMutation, generateRenewalYearQuoteMutation, employerRenewalConfig])

  return {
    generateRenewalQuotes,
    isLoading: generateCurrentYearQuoteMutation.isPending || generateRenewalYearQuoteMutation.isPending,
    isError: generateCurrentYearQuoteMutation.isError || generateRenewalYearQuoteMutation.isError || !!error,
    error,
  }
}
